import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import CommentCard from "../../components/comments/CommentCard";
import ImageThumbnailGallery from "../../components/ImageThumbnailGallery";
import MobileActionBarContent from "../../components/mobile-action-bar/MobileActionBarContent";
import ConfirmDialog from "../../components/modal/ConfirmDialog";
import LinkDeliveryNoteDialog from "../../components/modal/LinkDeliveryNoteDialog";
import SelectTemplateDialog from "../../components/modal/SelectTemplateDialog";
import Markdown from "../../components/misc/Markdown";
import Spinner from "../../components/misc/Spinner";
import PrevNextNav, { usePrevNextNav } from "../../components/PrevNextNav";
import { Role, useAuthStore } from "../../stores/auth";
import { DELIVERY_NOTE_STATE } from "../../stores/delivery_notes";
import { WORK_ORDER_STATUS, useWorkOrdersStore } from "../../stores/work_orders";
import { formatDate, formatDateTime, formatTime } from "../../utils/dateFormat";
import { formatRange } from "../../utils/rangeFormat";

const formatAddress = (addresses = []) => {
  const address = addresses[0];
  if (!address) return "";
  return [
    address.street,
    [address.zip, address.city].filter(Boolean).join(" "),
    address.district,
    address.country,
  ].filter(Boolean).join(", ");
};

function EntityColumn({ title, entity }) {
  return (
    <div className="col-md-6">
      <div className="text-muted small">{title}</div>
      {!_.isEmpty(entity?.number) && <div>{entity.number}</div>}
      {!_.isEmpty(entity?.name) && <div>{entity.name}</div>}
      <div>{formatAddress(entity?.addresses)}</div>
    </div>
  );
}

function WorkOrderDetails() {
  const { t } = useTranslation();
  const params = useParams();
  const workOrderId = Number.parseInt(params.workOrderId);
  const navigate = useNavigate();

  const { me, hasRole } = useAuthStore(
    useShallow((state) => ({ me: state.me, hasRole: state.hasRole })),
  );
  const isBackoffice = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);
  const isTechnician = hasRole(Role.TECHNICIAN);

  const {
    byIds,
    allIds,
    total,
    lastFetchParams,
    getById,
    fetchSome,
    assignMe,
    cancelWorkOrder,
    linkDeliveryNote,
    unlinkDeliveryNote,
    addComment,
    updateComment,
    deleteComment,
  } = useWorkOrdersStore(
    useShallow((state) => ({
      byIds: state.byIds,
      allIds: state.allIds,
      total: state.total,
      lastFetchParams: state.lastFetchParams,
      getById: state.getById,
      fetchSome: state.fetchSome,
      assignMe: state.assignMe,
      cancelWorkOrder: state.cancelWorkOrder,
      linkDeliveryNote: state.linkDeliveryNote,
      unlinkDeliveryNote: state.unlinkDeliveryNote,
      addComment: state.addComment,
      updateComment: state.updateComment,
      deleteComment: state.deleteComment,
    })),
  );

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [unlinkNote, setUnlinkNote] = useState(null);
  const [selectedRemark, setSelectedRemark] = useState(null);

  const isLoading = !allIds.includes(workOrderId);
  const workOrder = byIds[workOrderId];
  const status = workOrder?.status || WORK_ORDER_STATUS.OPEN;
  const isOpen = status === WORK_ORDER_STATUS.OPEN;
  const isActive = isOpen || status === WORK_ORDER_STATUS.IN_PROGRESS;
  const isUnassigned = !workOrder?.assigned_to;
  const isAssignedToMe = workOrder?.assigned_to?.id === me?.id;
  const canAssignMe = isTechnician && isOpen && isUnassigned;
  const isExternal = !!workOrder?.source && workOrder.source !== "portal";
  const deliveryNotes = workOrder?.delivery_notes || [];
  // one active note per order for technicians — mirrors the backend guard;
  // canceled notes stay linked but don't block a replacement
  const activeNote = deliveryNotes.find(
    (deliveryNote) => deliveryNote.current_state !== DELIVERY_NOTE_STATE.CANCELED,
  );
  const canCreateForm =
    isTechnician &&
    isActive &&
    (isUnassigned || isAssignedToMe) &&
    (isBackoffice || !activeNote);
  const canLinkExisting =
    isActive &&
    (isBackoffice ||
      (isTechnician && (isUnassigned || isAssignedToMe) && !activeNote));
  // delivered field work must not be discarded — mirrors the backend guard
  const hasDeliveredNote = deliveryNotes.some((deliveryNote) =>
    [
      DELIVERY_NOTE_STATE.DELIVERED,
      DELIVERY_NOTE_STATE.REVIEWED,
      DELIVERY_NOTE_STATE.BILLED,
    ].includes(deliveryNote.current_state),
  );
  const canCancel =
    isBackoffice && status !== WORK_ORDER_STATUS.CANCELED && !hasDeliveredNote;
  const untilPrefix = t("common.range_until");
  const scheduled = formatRange({
    from: formatDate(workOrder?.scheduled_from),
    until: formatDate(workOrder?.scheduled_until),
    untilPrefix,
  });
  const availability = formatRange({
    from: formatTime(workOrder?.availability_from),
    until: formatTime(workOrder?.availability_until),
    fromPrefix: t("common.range_from"),
    untilPrefix,
  });
  const buildWorkOrderUrl = useCallback((id) => `/work_orders/${id}/details`, []);

  const {
    prevId: prevNavId,
    nextId: nextNavId,
    hasPrevPage,
    hasNextPage,
    navigatePrev,
    navigateNext,
    visible: navVisible,
  } = usePrevNextNav({
    currentId: workOrderId,
    allIds,
    total,
    lastFetchParams,
    fetchPage: fetchSome,
    buildUrl: buildWorkOrderUrl,
  });

  const listUrl = lastFetchParams
    ? `/work_orders?${new URLSearchParams({
        page: Math.floor(lastFetchParams.offset / lastFetchParams.limit),
        ...(lastFetchParams.filter && { filter: lastFetchParams.filter }),
        ...(lastFetchParams.q && { q: lastFetchParams.q }),
      })}`
    : "/work_orders";

  useEffect(() => {
    getById({ id: workOrderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  if (isLoading || !workOrder) {
    return (
      <div>
        <Breadcrumb items={[{ label: t("work_orders.work_orders"), to: listUrl }, { label: "…" }]}>
          <PrevNextNav
            currentId={workOrderId}
            allIds={allIds}
            total={total}
            lastFetchParams={lastFetchParams}
            fetchPage={fetchSome}
            buildUrl={buildWorkOrderUrl}
          />
        </Breadcrumb>
        <Spinner />
      </div>
    );
  }

  const handleAssignMe = async () => {
    await assignMe({ id: workOrder.id });
  };

  const handleCancel = async () => {
    setShowConfirmCancel(false);
    await cancelWorkOrder({ id: workOrder.id });
  };

  const handleLink = async (deliveryNoteId) => {
    const result = await linkDeliveryNote({ id: workOrder.id, delivery_note_id: deliveryNoteId });
    if (result.data) {
      setShowLinkDialog(false);
    }
  };

  const handleUnlink = async () => {
    const note = unlinkNote;
    setUnlinkNote(null);
    await unlinkDeliveryNote({ id: workOrder.id, deliveryNoteId: note.id });
  };

  const addFormUrl = (templateId) =>
    `/delivery_notes/add?templateId=${templateId}&workOrderId=${workOrder.id}`;

  const noteUrl = (deliveryNote) => {
    const state = deliveryNote.current_state || DELIVERY_NOTE_STATE.DRAFTED;
    return state === DELIVERY_NOTE_STATE.DRAFTED && isTechnician && !isBackoffice
      ? `/delivery_notes/${deliveryNote.id}/edit`
      : `/delivery_notes/${deliveryNote.id}/details`;
  };

  const canOpenForm = isTechnician && !isBackoffice && Boolean(activeNote);
  const activeNoteUrl = activeNote ? noteUrl(activeNote) : null;

  const canUnlinkNote = (deliveryNote) =>
    isActive &&
    (isBackoffice ||
      deliveryNote.created_by?.id === me?.id ||
      deliveryNote.performed_by?.id === me?.id);

  const showUpdatedBy =
    workOrder.updated_by && workOrder.updated_by.id !== workOrder.created_by?.id;
  const hasRemarkSummary = Boolean(workOrder.remark_summary);
  const showRemarkSummary = hasRemarkSummary && selectedRemark !== "original";
  const displayedRemark = showRemarkSummary ? workOrder.remark_summary : workOrder.remark;
  const originalRemarkButtonClass = `btn btn-xs btn-default${showRemarkSummary ? "" : " active"}`;
  const summaryRemarkButtonClass = `btn btn-xs btn-default${showRemarkSummary ? " active" : ""}`;

  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("work_orders.work_orders"), to: listUrl },
          { label: `${t("work_orders.number")} ${workOrder.number}` },
        ]}
      >
        <div className="d-flex gap-2 align-items-center">
          <div className="btn-group">
            {canAssignMe && (
              <button type="button" className="btn btn-sm btn-white" onClick={handleAssignMe}>
                <i className="fa fa-user-check fa-fw me-1" />{t("work_orders.assign_to_me")}
              </button>
            )}
            {isBackoffice && isActive && (
              <Link to={`/work_orders/${workOrder.id}/edit`} className="btn btn-sm btn-white">
                <i className="fa fa-pencil fa-fw me-1" />{t("common.edit")}
              </Link>
            )}
            {canCancel && (
              <button
                type="button"
                className="btn btn-sm btn-white text-danger"
                onClick={() => setShowConfirmCancel(true)}
              >
                <i className="fa fa-ban fa-fw me-1" />{t("work_orders.cancel")}
              </button>
            )}
          </div>
          {navVisible && (
            <div className="btn-group d-none d-md-flex">
              <button
                type="button"
                className="btn btn-white btn-sm"
                disabled={prevNavId === null && !hasPrevPage}
                onClick={navigatePrev}
              >
                <i className="fa fa-chevron-left" />
              </button>
              <button
                type="button"
                className="btn btn-white btn-sm"
                disabled={nextNavId === null && !hasNextPage}
                onClick={navigateNext}
              >
                <i className="fa fa-chevron-right" />
              </button>
            </div>
          )}
        </div>
      </Breadcrumb>

      <MobileActionBarContent>
        <div className="d-flex gap-2">
          {canOpenForm && (
            <Link to={activeNoteUrl} className="btn btn-red flex-fill">
              {t("work_orders.open_form")}
            </Link>
          )}
          {canCreateForm && (
            <button
              type="button"
              className="btn btn-red flex-fill"
              onClick={() => setShowTemplateDialog(true)}
            >
              {t("work_orders.create_form")}
            </button>
          )}
          {canAssignMe && <button type="button" className="btn btn-default flex-fill" onClick={handleAssignMe}>{t("work_orders.assign_to_me")}</button>}
          {isBackoffice && isActive && (
            <Link to={`/work_orders/${workOrder.id}/edit`} className="btn btn-success flex-fill">
              <i className="fa fa-pencil fa-fw me-1" />
              {t("common.edit")}
            </Link>
          )}
          <div className="btn-group ms-auto">
            <button
              type="button"
              className="btn btn-default"
              disabled={!prevNavId && !hasPrevPage}
              onClick={navigatePrev}
            >
              <i className="fa fa-chevron-left" />
            </button>
            <button
              type="button"
              className="btn btn-default"
              disabled={!nextNavId && !hasNextPage}
              onClick={navigateNext}
            >
              <i className="fa fa-chevron-right" />
            </button>
          </div>
          <Link to={listUrl} className="btn btn-danger" title={t("common.close")}><i className="fa fa-times" /></Link>
        </div>
      </MobileActionBarContent>

      {isExternal && (
        <div className="alert alert-info">
          <i className="fa fa-circle-info me-2" />
          {t(`work_orders.origin_hint.${workOrder.source}`)}
        </div>
      )}

      <div className="row">
        <div className="col-12 col-lg-8">
          <div className="card border-0 mb-4">
            <div className="card-header h6 mb-0 bg-none p-3">
              <i className="fa fa-clipboard-list fa-lg fa-fw text-dark text-opacity-50 me-1" />
              {t("work_orders.work_order")}
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="text-muted small">{t("work_orders.number")}</div>
                  <div className="fw-bold">{workOrder.number}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">{t("work_orders.status")}</div>
                  <div>{t(`work_orders.states.${status}`)}</div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="text-muted small">{t("work_orders.scheduled")}</div>
                  <div>{scheduled || "—"}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">{t("work_orders.availability")}</div>
                  <div>{availability || "—"}</div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="text-muted small">{t("work_orders.assigned_to")}</div>
                  <div>{workOrder.assigned_to?.name || <span className="text-muted">{t("work_orders.unassigned")}</span>}</div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted small">{t("work_orders.assigned_at")}</div>
                  <div>{formatDateTime(workOrder.assigned_at) || "—"}</div>
                </div>
              </div>
              <div className="row mb-3">
                <EntityColumn title={t("customers.customer")} entity={workOrder.customer} />
                <EntityColumn title={t("customers.properties.property")} entity={workOrder.customer_property} />
              </div>
              <div>
                <div className="d-flex align-items-center mb-1">
                  <div className="text-muted small">{t("work_orders.remark")}</div>
                  {hasRemarkSummary && (
                    <div className="btn-group ms-2" role="group" aria-label={t("work_orders.remark")}>
                      <button
                        type="button"
                        className={originalRemarkButtonClass}
                        aria-pressed={!showRemarkSummary}
                        onClick={() => setSelectedRemark("original")}
                      >
                        {t("work_orders.remark_original")}
                      </button>
                      <button
                        type="button"
                        className={summaryRemarkButtonClass}
                        aria-pressed={showRemarkSummary}
                        onClick={() => setSelectedRemark("summary")}
                      >
                        {t("work_orders.remark_summary")}
                      </button>
                    </div>
                  )}
                </div>
                {displayedRemark ? <Markdown text={displayedRemark} /> : <span className="text-muted">—</span>}
              </div>
            </div>
          </div>

          {workOrder.files?.length > 0 && (
            <div className="card border-0 mb-4">
              <div className="card-header h6 mb-0 bg-none p-3">
                <i className="fa fa-paperclip fa-lg fa-fw text-dark text-opacity-50 me-1" />
                {t("menu.files")}
              </div>
              <div className="card-body">
                <div className="float-start">
                  <ImageThumbnailGallery
                    files={workOrder.files}
                    showFileDetails={false}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="col-12 col-lg-4">
          <div className="card border-0 mb-4">
            <div className="card-header h6 mb-0 bg-none p-3">{t("work_orders.audit")}</div>
            <div className="card-body">
              <div className={showUpdatedBy || workOrder.canceled_by ? "mb-2" : ""}>
                <span className="text-muted">{t("common.created_by")}:</span> {workOrder.created_by?.name}{" "}
                {t("common.on_date")} {formatDateTime(workOrder.created_at)}
              </div>
              {showUpdatedBy && (
                <div className={workOrder.canceled_by ? "mb-2" : ""}>
                  <span className="text-muted">{t("common.updated_by")}:</span> {workOrder.updated_by.name}{" "}
                  {t("common.on_date")} {formatDateTime(workOrder.updated_at)}
                </div>
              )}
              {workOrder.canceled_by && (
                <div>
                  <span className="text-muted">{t("work_orders.canceled_by")}:</span> {workOrder.canceled_by.name}{" "}
                  {t("common.on_date")} {formatDateTime(workOrder.canceled_at)}
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 mb-4">
            <div className="card-header h6 mb-0 bg-none p-3">
              <i className="fa fa-file-lines fa-lg fa-fw text-dark text-opacity-50 me-1" />
              {t("delivery_notes.delivery_notes")}
            </div>
            <div className="card-body">
              {deliveryNotes.length > 0 && (
                <ul className="list-group list-group-flush">
                  {deliveryNotes.map((deliveryNote) => {
                    const state = deliveryNote.current_state || DELIVERY_NOTE_STATE.DRAFTED;
                    return (
                      <li
                        key={deliveryNote.id}
                        className="list-group-item d-flex justify-content-between align-items-center px-0"
                      >
                        <span>
                          <Link to={noteUrl(deliveryNote)} className="fw-bold me-2">
                            {deliveryNote.number}
                          </Link>
                          <span className="badge bg-default text-dark">
                            {t(`delivery_notes.states.${state}`)}
                          </span>
                        </span>
                        {canUnlinkNote(deliveryNote) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-white text-danger"
                            title={t("work_orders.unlink")}
                            onClick={() => setUnlinkNote(deliveryNote)}
                          >
                            <i className="fa fa-link-slash" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {(canCreateForm || canLinkExisting) && (
                <div className={deliveryNotes.length > 0 ? "d-flex gap-2 mt-3" : "d-flex gap-2"}>
                  {canCreateForm && (
                    <button
                      type="button"
                      className="btn btn-sm btn-red flex-fill flex-xxl-grow-0 d-none d-md-inline-block"
                      onClick={() => setShowTemplateDialog(true)}
                    >
                      <i className="fa fa-file-lines fa-fw me-1" />{t("work_orders.create")}
                    </button>
                  )}
                  {canLinkExisting && (
                    <button type="button" className="btn btn-sm btn-white flex-fill flex-xxl-grow-0" onClick={() => setShowLinkDialog(true)}>
                      <i className="fa fa-link fa-fw me-1" />{t("work_orders.link")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <CommentCard
            key={workOrder.id}
            comments={workOrder.comments || []}
            canManageComment={(comment) => me?.id === comment.created_by?.id || hasRole(Role.ADMINISTRATOR)}
            onAdd={(text) => addComment({ id: workOrder.id, text })}
            onUpdate={(commentId, text) => updateComment({ id: workOrder.id, commentId, text })}
            onDelete={(commentId) => deleteComment({ id: workOrder.id, commentId })}
          />
        </div>
      </div>
      {showConfirmCancel && (
        <ConfirmDialog
          title={t("work_orders.cancel")}
          message={t("work_orders.confirm_cancel")}
          confirmLabel={t("work_orders.cancel")}
          confirmClassName="btn btn-danger"
          onCancel={() => setShowConfirmCancel(false)}
          onConfirm={handleCancel}
        />
      )}
      {unlinkNote && (
        <ConfirmDialog
          title={t("work_orders.unlink")}
          message={t("work_orders.confirm_unlink", { number: unlinkNote.number })}
          confirmLabel={t("work_orders.unlink")}
          confirmClassName="btn btn-danger"
          onCancel={() => setUnlinkNote(null)}
          onConfirm={handleUnlink}
        />
      )}
      {showTemplateDialog && (
        <SelectTemplateDialog
          onSelect={(templateId) => navigate(addFormUrl(templateId))}
          onClose={() => setShowTemplateDialog(false)}
        />
      )}
      {showLinkDialog && (
        <LinkDeliveryNoteDialog
          onSelect={handleLink}
          onClose={() => setShowLinkDialog(false)}
        />
      )}
    </div>
  );
}

export default WorkOrderDetails;
